// SOARLIFE FERTILITY ROADMAP™ — FRANÇAIS (French)
// Mirrors roadmapContent.js structure exactly — same ids, keys, and shape.

export const EVALUATION = {
  id: "evaluation",
  title: "Évaluation de la Fertilité",
  intro:
    "La plupart des cliniques de fertilité commencent par des examens avant le traitement. Comprendre vos résultats vous aide, vous et votre médecin, à choisir la bonne voie.",
  groups: [
    {
      label: "Les examens pour la femme peuvent inclure",
      items: [
        "AMH (Hormone Anti-Müllérienne)",
        "FSH",
        "LH",
        "Estradiol",
        "Progestérone",
        "TSH",
        "Vitamine D",
        "Dépistage des maladies infectieuses",
        "Échographie pelvienne",
        "HSG",
        "Sonohystérographie",
        "Dépistage génétique des porteurs",
      ],
    },
    {
      label: "Les examens pour l'homme peuvent inclure",
      items: [
        "Spermogramme",
        "Testostérone",
        "Bilan hormonal",
        "Dépistage des maladies infectieuses",
        "Dépistage génétique des porteurs",
      ],
    },
  ],
  checklist: [
    { key: "eval:health-history", label: "Compléter l'historique médical" },
    { key: "eval:upload-records", label: "Téléverser les dossiers" },
    { key: "eval:upload-medications", label: "Téléverser les médicaments" },
    { key: "eval:verify-insurance", label: "Vérifier l'assurance" },
    { key: "eval:provider-questions", label: "Ajouter des questions pour le médecin" },
  ],
};

export const AMH_EDUCATION = {
  id: "amh",
  title: "Comprendre l'AMH",
  paragraphs: [
    "AMH signifie Hormone Anti-Müllérienne.",
    "L'AMH aide à estimer la réserve ovarienne, c'est-à-dire le nombre d'ovocytes restant dans les ovaires.",
    "L'AMH ne mesure pas la qualité des ovocytes.",
  ],
  groups: [
    {
      label: "L'AMH peut aider les médecins à estimer",
      items: ["La réserve ovarienne", "La réponse aux médicaments de fertilité", "La planification de la FIV"],
    },
  ],
  notes: [
    { heading: "AMH élevée", text: "Peut indiquer plus de follicules disponibles." },
    { heading: "AMH basse", text: "Peut indiquer une réserve ovarienne diminuée." },
    {
      heading: "À retenir",
      text: "Une AMH basse ne signifie pas automatiquement qu'une grossesse est impossible.",
    },
  ],
  suggestedQuestions: [
    "Comment mon AMH affecte-t-elle mon traitement ?",
    "Devrais-je envisager la préservation de ma fertilité ?",
    "Quels examens supplémentaires sont recommandés ?",
  ],
};

export const PRE_TRANSFER = {
  id: "pre-transfer",
  title: "Préparation au Transfert d'Embryon",
  paragraphs: ["De nombreux spécialistes évaluent l'utérus avant le transfert."],
  groups: [
    { label: "Les examens peuvent inclure", items: ["Sonohystérographie", "Hystéroscopie"] },
  ],
  notes: [
    {
      heading: "Qu'est-ce qu'une hystéroscopie",
      text: "Une procédure permettant au médecin de visualiser l'intérieur de la cavité utérine.",
    },
    {
      heading: "L'objectif peut être d'identifier",
      text: "Polypes · Cicatrices · Fibromes affectant la cavité utérine · Zones d'inflammation",
    },
  ],
  checklist: [
    { key: "transfer:review-meds", label: "Réviser les médicaments" },
    { key: "transfer:follow-instructions", label: "Suivre les instructions du transfert" },
    { key: "transfer:confirm-date", label: "Confirmer la date du transfert" },
    { key: "transfer:transportation", label: "Organiser le transport" },
    { key: "transfer:prep-checklist", label: "Compléter la liste de préparation" },
    { key: "transfer:support", label: "Prévoir un soutien si souhaité" },
  ],
};

export const BLOCKED_TUBES = {
  id: "blocked-tubes",
  title: "Comprendre les Trompes Bouchées",
  paragraphs: [],
  groups: [
    {
      label: "Causes possibles des trompes bouchées",
      items: ["Infection antérieure", "Endométriose", "Cicatrices", "Chirurgie", "Hydrosalpinx"],
    },
  ],
  notes: [
    {
      heading: "Peut-on déboucher les trompes ? Parfois",
      text: "Un médecin peut proposer une chirurgie laparoscopique pour tenter une réparation.",
    },
    {
      heading: "Cependant",
      text: "Le succès n'est pas garanti — les résultats dépendent de la sévérité et de l'emplacement de l'obstruction.",
    },
    {
      heading: "Prochaines étapes",
      text: "Certaines patientes peuvent être orientées vers la FIV.",
    },
  ],
};

export const DONOR_EGG_EDU = {
  id: "donor-egg-edu",
  title: "Centre d'Éducation sur le Don d'Ovocytes",
  paragraphs: [
    "Avec l'âge, la qualité des ovocytes et la normalité chromosomique diminuent généralement.",
    "Recourir au don d'ovocytes est une décision personnelle.",
  ],
  groups: [
    {
      label: "Pourquoi les médecins évoquent souvent le don d'ovocytes après 44 ans",
      items: [
        "Les taux de développement embryonnaire peuvent diminuer",
        "Les taux de fausse couche peuvent augmenter",
        "Les taux de réussite de FIV avec ses propres ovocytes deviennent souvent plus faibles",
        "Le don d'ovocytes peut offrir une plus grande chance de grossesse",
      ],
    },
  ],
  notes: [],
  soarlifeProvides: [
    "Éducation",
    "Soutien émotionnel",
    "Préparation aux rendez-vous",
    "Questions à discuter avec votre médecin",
  ],
};

export const PREGNANCY_TESTING = {
  id: "pregnancy-testing",
  title: "Test de Grossesse & L'Attente",
  paragraphs: [],
  groups: [
    {
      label: "Après une IUI",
      items: ["Le test de grossesse sanguin a lieu environ 2 semaines plus tard"],
    },
    {
      label: "Après un transfert FIV",
      items: ["Le test de grossesse sanguin (Beta hCG) est réalisé selon le protocole de la clinique"],
    },
    {
      label: "Pendant l'attente, votre portail vous offre",
      items: [
        "Encouragement quotidien",
        "Soutien au bien-être émotionnel",
        "Éducation par IA",
        "Options de soutien dirigées par une infirmière",
      ],
    },
  ],
  notes: [],
};

export const PATHWAYS = [
  {
    id: "natural",
    title: "Essayer de Concevoir Naturellement",
    tagline: "Comprendre votre cycle et votre fenêtre fertile",
    whatIsIt: "Suivi du cycle naturel sans médicaments de fertilité.",
    whatToExpect: [
      {
        label: "Mois 1",
        items: ["Examens de base", "Suivi de l'ovulation"],
      },
      {
        label: "Chaque mois",
        items: ["Suivi de la fertilité", "Optimisation du mode de vie", "Éducation sur le bon timing"],
      },
    ],
    doctorQuestions: [
      "Est-ce que j'ovule régulièrement ?",
      "Ai-je besoin d'examens supplémentaires ?",
      "Mon partenaire devrait-il faire un spermogramme ?",
      "Combien de temps devrions-nous continuer à essayer naturellement ?",
    ],
    steps: [
      {
        title: "Évaluation de la Fertilité",
        summary: "Examens de base avant le traitement",
        details: ["La plupart des cliniques commencent par des examens avant le traitement."],
        education: ["evaluation", "amh"],
      },
      {
        title: "Suivi de l'Ovulation",
        summary: "Apprenez les schémas de votre cycle",
        details: [
          "Suivez l'ovulation à chaque cycle — les tests d'ovulation et les signes du cycle aident à identifier le bon moment.",
        ],
      },
      {
        title: "Fenêtre Fertile",
        summary: "Surveillez les jours où la conception est possible",
        details: [
          "Surveillez la fenêtre fertile — généralement les 5 jours précédant l'ovulation et le jour de l'ovulation.",
        ],
      },
      {
        title: "Rapports Programmés",
        summary: "Autour de l'ovulation",
        details: ["Des rapports programmés autour de l'ovulation offrent la meilleure chance naturelle."],
      },
      {
        title: "Test de Grossesse",
        summary: "Quand et comment tester",
        details: ["La plupart des tests sont fiables dès le premier jour de retard des règles."],
        education: ["pregnancy-testing"],
      },
    ],
    checklist: [
      { key: "natural:track-cycles", label: "Suivre les cycles" },
      { key: "natural:track-ovulation", label: "Suivre l'ovulation" },
      { key: "natural:testing", label: "Compléter les examens de fertilité" },
      { key: "natural:symptoms", label: "Noter les symptômes" },
    ],
  },
  {
    id: "ti",
    title: "Rapports Programmés (TI)",
    tagline: "Identifier l'ovulation et programmer la fenêtre fertile",
    whatIsIt:
      "Les rapports programmés consistent à identifier l'ovulation et à avoir des rapports pendant la fenêtre fertile.",
    doctorQuestions: [
      "Est-ce que j'ovule régulièrement ?",
      "Ai-je besoin d'examens supplémentaires ?",
      "Mon partenaire devrait-il faire un spermogramme ?",
      "Combien de temps devrions-nous continuer les rapports programmés ?",
    ],
    steps: [
      {
        title: "Suivi de l'Ovulation",
        summary: "Étape 1 du parcours TI",
        details: ["Identifiez l'ovulation par le suivi, les tests ou le monitoring en clinique."],
        education: ["evaluation"],
      },
      {
        title: "Fenêtre Fertile",
        summary: "Étape 2 du parcours TI",
        details: ["Votre médecin peut confirmer le moment de l'ovulation par suivi ou analyses."],
      },
      {
        title: "Rapports Programmés",
        summary: "Étape 3 du parcours TI",
        details: ["Les rapports sont planifiés autour de la fenêtre fertile confirmée."],
      },
      {
        title: "Test de Grossesse",
        summary: "Étape 4 du parcours TI",
        details: ["Le test a lieu environ deux semaines après l'ovulation."],
        education: ["pregnancy-testing"],
      },
    ],
    checklist: [
      { key: "ti:track-cycles", label: "Suivre les cycles" },
      { key: "ti:track-ovulation", label: "Suivre l'ovulation" },
      { key: "ti:testing", label: "Compléter les examens de fertilité" },
      { key: "ti:symptoms", label: "Noter les symptômes" },
    ],
  },
  {
    id: "medicated-ti",
    title: "Rapports Programmés avec Médicaments",
    tagline: "Soutien du cycle avec médicaments de fertilité",
    steps: [
      {
        title: "Base (Jours 2-5 du cycle)",
        summary: "Échographie de base + bilan sanguin",
        details: ["Échographie de base", "Bilan sanguin"],
        education: ["evaluation", "amh"],
      },
      {
        title: "Médicaments (Jours 3-7 du cycle)",
        summary: "Phase de médication",
        details: [],
        medicationCards: [
          { name: "Clomid", desc: "Stimule l'ovulation." },
          { name: "Létrozole", desc: "Souvent prescrit pour les troubles de l'ovulation, y compris le SOPK." },
          { name: "Ovidrel (déclencheur)", desc: "Déclenche le moment de l'ovulation." },
        ],
      },
      {
        title: "Suivi (Jours 10-14 du cycle)",
        summary: "Échographie de suivi + bilan hormonal",
        details: [
          "Échographie de suivi",
          "Suivi hormonal",
          "Des rappels automatiques pour les médicaments et échographies sont envoyés par email avant vos rendez-vous.",
        ],
      },
      {
        title: "Injection Déclencheur",
        summary: "Ovidrel administré",
        details: ["Ovidrel est administré pour préciser le moment de l'ovulation."],
      },
      {
        title: "Rapports Programmés",
        summary: "Selon les recommandations du médecin",
        details: ["Le timing suit les recommandations de votre médecin après l'injection."],
      },
      {
        title: "Test de Grossesse",
        summary: "Environ deux semaines plus tard",
        details: ["Votre clinique vous indiquera le jour exact du test."],
        education: ["pregnancy-testing"],
      },
    ],
    checklist: [
      { key: "mti:baseline", label: "Compléter la visite de base" },
      { key: "mti:meds", label: "Prendre les médicaments comme prescrits" },
      { key: "mti:monitoring", label: "Assister aux échographies de suivi" },
      { key: "mti:trigger", label: "Injection déclencheur faite" },
    ],
    prepareChecklist: {
      title: "À Préparer",
      items: [
        { key: "mti:reminders", label: "Rappels de médicaments activés" },
        { key: "mti:calendar", label: "Calendrier mis à jour" },
        { key: "mti:transport", label: "Transport organisé" },
        { key: "mti:questions", label: "Questions enregistrées" },
      ],
    },
  },
  {
    id: "iui",
    title: "IUI (Insémination Intra-Utérine)",
    tagline: "Le parcours IUI, étape par étape",
    doctorQuestions: [
      "Quand devrais-je m'attendre à ovuler ?",
      "Combien de follicules se sont développés ?",
      "Quelles sont les chances de succès ?",
      "Que se passe-t-il si ce cycle ne fonctionne pas ?",
    ],
    steps: [
      {
        title: "Avant de Commencer l'IUI",
        summary: "Condition importante",
        details: [
          "Au moins une trompe de Fallope doit être perméable.",
          "Les examens peuvent inclure : HSG, Sonohystérographie.",
        ],
        education: ["evaluation", "blocked-tubes"],
      },
      {
        title: "Rendez-vous de Base",
        summary: "Échographie + bilan sanguin",
        details: ["Échographie", "Bilan sanguin"],
      },
      {
        title: "Stimulation de l'Ovulation",
        summary: "Médicaments possibles",
        details: [],
        medicationCards: [
          { name: "Létrozole", desc: "Souvent prescrit pour soutenir l'ovulation." },
          { name: "Clomid", desc: "Stimule l'ovulation." },
          { name: "Médicaments injectables", desc: "Utilisés quand une réponse plus forte est nécessaire." },
        ],
      },
      {
        title: "Rendez-vous de Suivi",
        summary: "Échographies + bilan hormonal",
        details: ["Échographies", "Bilan hormonal"],
      },
      {
        title: "Injection Déclencheur",
        summary: "Ovidrel",
        details: ["Ovidrel synchronise l'ovulation pour la procédure."],
      },
      {
        title: "Procédure IUI",
        summary: "Jour de l'insémination",
        details: ["Le sperme préparé est placé directement dans l'utérus."],
      },
      {
        title: "Deux Semaines d'Attente",
        summary: "Votre portail vous soutient pendant cette période",
        details: [
          "Encouragement quotidien",
          "Journal des symptômes (notes de bien-être)",
          "Bilans de bien-être",
          "Soutien éducatif",
        ],
      },
      {
        title: "Test de Grossesse",
        summary: "Environ 2 semaines après l'IUI",
        details: ["Le test de grossesse sanguin a lieu environ 2 semaines plus tard."],
        education: ["pregnancy-testing"],
      },
    ],
    checklist: [
      { key: "iui:tubes", label: "Examen des trompes fait (HSG / sonohystérographie)" },
      { key: "iui:monitoring", label: "Assister aux rendez-vous de suivi" },
      { key: "iui:procedure", label: "Procédure IUI faite" },
      { key: "iui:wellness", label: "Bilans de bien-être quotidiens pendant l'attente" },
    ],
  },
  {
    id: "ivf",
    title: "FIV (Fécondation In Vitro)",
    tagline: "Le parcours FIV complet en sept étapes",
    steps: [
      {
        title: "Étape 1 · Examens & Préparation",
        summary: "Les examens requis peuvent inclure",
        details: [
          "Bilan hormonal",
          "AMH",
          "Vitamine D",
          "Dépistage des maladies infectieuses",
          "Dépistage génétique",
          "Spermogramme",
          "Évaluation échographique",
        ],
        education: ["evaluation", "amh"],
      },
      {
        title: "Étape 2 · Stimulation Ovarienne",
        summary: "Les médicaments injectables quotidiens peuvent inclure",
        details: [],
        medicationCards: [
          { name: "Gonal-F", desc: "Médicament de stimulation injectable quotidien." },
          { name: "Follistim", desc: "Médicament de stimulation injectable quotidien." },
          { name: "Menopur", desc: "Médicament de stimulation injectable quotidien." },
        ],
        extraGroups: [
          {
            label: "Visites de suivi",
            items: ["Bilans sanguins", "Échographies", "Ajustements des médicaments"],
          },
        ],
      },
      {
        title: "Étape 3 · Injection Déclencheur",
        summary: "Maturation finale des ovocytes avant le prélèvement",
        details: ["L'injection déclencheur finalise la maturation des ovocytes avant le prélèvement."],
      },
      {
        title: "Étape 4 · Prélèvement des Ovocytes",
        summary: "Procédure sous sédation",
        details: ["Le prélèvement des ovocytes est une procédure réalisée sous sédation pour recueillir les ovocytes matures."],
      },
      {
        title: "Étape 5 · Fécondation",
        summary: "Les ovocytes sont combinés avec le sperme au laboratoire",
        details: ["Les ovocytes et le sperme sont combinés dans un laboratoire."],
      },
      {
        title: "Étape 6 · Développement Embryonnaire",
        summary: "Observer la croissance des embryons",
        details: [
          "Jour 1 — Rapport de fécondation",
          "Jour 3 — Mise à jour du développement",
          "Jours 5-7 — Développement des blastocystes",
        ],
      },
      {
        title: "Étape 7 · Test Génétique (Optionnel)",
        summary: "Le PGT peut être discuté selon les circonstances individuelles",
        details: [
          "Avantages — dépiste les anomalies chromosomiques des embryons avant le transfert.",
          "Limites — pas une garantie de grossesse ; discutez de la précision et des résultats avec votre médecin.",
          "Coûts — souvent facturés séparément ; vérifiez votre couverture.",
          "Utilisez le générateur de questions ci-dessous pour préparer vos questions.",
        ],
        education: ["pre-transfer", "pregnancy-testing"],
      },
    ],
    checklist: [
      { key: "ivf:testing", label: "Compléter tous les examens de base" },
      { key: "ivf:stimulation", label: "Suivis de stimulation effectués" },
      { key: "ivf:retrieval", label: "Prélèvement des ovocytes fait" },
      { key: "ivf:transfer-prep", label: "Liste de préparation au transfert complétée" },
    ],
  },
  {
    id: "egg-freezing",
    title: "Congélation d'Ovocytes",
    tagline: "Préserver votre fertilité à votre rythme",
    steps: [
      {
        title: "Évaluation de la Fertilité",
        summary: "Bilan de réserve et hormonal",
        details: ["L'AMH et les bilans hormonaux guident votre plan de préservation."],
        education: ["evaluation", "amh"],
      },
      {
        title: "Stimulation Ovarienne",
        summary: "Médicaments et suivi",
        details: ["Comme pour la FIV — bilans sanguins et échographies suivent la croissance des follicules."],
      },
      {
        title: "Prélèvement des Ovocytes",
        summary: "Procédure sous sédation",
        details: ["Le prélèvement des ovocytes est une procédure réalisée sous sédation."],
      },
      {
        title: "Congélation & Stockage",
        summary: "Vitrification et stockage",
        details: ["Les ovocytes matures sont congelés et conservés pour une utilisation future."],
      },
    ],
    checklist: [
      { key: "ef:testing", label: "Compléter le bilan de réserve" },
      { key: "ef:retrieval", label: "Prélèvement des ovocytes fait" },
      { key: "ef:storage", label: "Plan de stockage confirmé" },
    ],
  },
  {
    id: "donor-egg",
    title: "FIV avec Don d'Ovocytes",
    tagline: "Comprendre et naviguer la conception par don",
    steps: [
      {
        title: "Centre d'Éducation sur le Don d'Ovocytes",
        summary: "L'éducation d'abord",
        details: ["Recourir au don d'ovocytes est une décision personnelle — apprenez avant de décider."],
        education: ["donor-egg-edu"],
      },
      {
        title: "Choix de la Donneuse",
        summary: "Choisir une donneuse",
        details: ["Votre clinique ou agence guide le matching, le dépistage et les étapes légales."],
      },
      {
        title: "Préparation Utérine",
        summary: "Préparer le transfert",
        details: ["Les médicaments préparent la muqueuse utérine pour le transfert."],
        education: ["pre-transfer"],
      },
      {
        title: "Transfert d'Embryon",
        summary: "Jour du transfert",
        details: ["L'embryon est transféré dans l'utérus lors d'une procédure brève."],
      },
      {
        title: "Test de Grossesse",
        summary: "Jour du résultat",
        details: ["Le test sanguin suit le protocole de la clinique après l'attente."],
        education: ["pregnancy-testing"],
      },
    ],
    checklist: [
      { key: "de:education", label: "Lire l'éducation sur le don d'ovocytes" },
      { key: "de:donor", label: "Donneuse choisie" },
      { key: "de:prep", label: "Préparation utérine terminée" },
    ],
  },
  {
    id: "exploring",
    title: "Explorer les Options de Fertilité",
    tagline: "Apprenez d'abord, décidez quand vous êtes prête",
    steps: [
      {
        title: "Évaluation de la Fertilité",
        summary: "Connaître votre point de départ",
        details: ["Les examens vous donnent de l'information — pas d'obligation."],
        education: ["evaluation", "amh"],
      },
      {
        title: "Découvrir les Parcours",
        summary: "TI, IUI, FIV, préservation, don",
        details: ["Explorez chaque parcours à votre rythme dans cette feuille de route."],
        education: ["blocked-tubes"],
      },
      {
        title: "Questions Pour Votre Médecin",
        summary: "Préparez votre consultation",
        details: ["Utilisez le générateur de questions IA pour arriver préparée."],
      },
      {
        title: "Choisir avec un Soutien",
        summary: "Soarlife marche avec vous",
        details: ["Une accompagnante dirigée par une infirmière peut assister à vos rendez-vous, en ligne ou en personne."],
      },
    ],
    checklist: [
      { key: "ex:evaluation", label: "Compléter l'évaluation de fertilité" },
      { key: "ex:questions", label: "Créer des questions pour le médecin" },
      { key: "ex:consult", label: "Réserver une consultation nouvelle patiente" },
    ],
  },
];

export const EDUCATION_SECTIONS = {
  evaluation: EVALUATION,
  amh: AMH_EDUCATION,
  "pre-transfer": PRE_TRANSFER,
  "blocked-tubes": BLOCKED_TUBES,
  "donor-egg-edu": DONOR_EGG_EDU,
  "pregnancy-testing": PREGNANCY_TESTING,
};

export const ACTION_CENTER = {
  supportOptions: [
    "Accompagnante Fertilité en Ligne",
    "Accompagnante Fertilité en Personne à Orlando",
    "Interprète Créole Haïtien",
    "Session de Soutien Familial",
  ],
  languages: ["English", "Kreyòl Ayisyen", "Français"],
};

export const UI = {
  portal: "Portail",
  tagline: "Votre centre de navigation du traitement",
  scoreTitle: "Score de Préparation du Parcours",
  scoreParts: {
    history: "Historique médical complété",
    documents: "Documents téléversés",
    questions: "Questions préparées",
    appointments: "Rendez-vous confirmés",
    medications: "Médicaments suivis",
    wellness: "Bilans de bien-être faits",
  },
  choosePathway: "Choisissez votre parcours",
  whereAreYou: "Où en êtes-vous dans votre parcours",
  pickHint: "Sélectionnez le parcours qui vous correspond — vous pouvez le changer à tout moment.",
  yourPathway: "Votre parcours",
  changePathway: "Changer de parcours",
  pathwayProgress: "Progression du parcours",
  whatToExpectSteps: "À quoi s'attendre — étape par étape",
  checklistSuffix: "liste de contrôle",
  whatIsIt: "Qu'est-ce que c'est ?",
  whatToExpect: "À quoi s'attendre",
  doctorQuestions: "Questions à poser à votre médecin",
  generateQuestions: "Générer des questions pour mon médecin",
  questionsForProvider: "Questions pour votre médecin",
  couldNotGenerate: "Impossible de générer les questions pour le moment — veuillez réessayer.",
  suggestedQuestionsLabel: "Questions suggérées",
  actionCenter: "Centre d'Action Soarlife",
  acExpect: "À Quoi s'Attendre",
  acQuestions: "Questions Pour Mon Médecin",
  acAppointments: "Mes Rendez-vous",
  acReminders: "Rappels de Médicaments",
  acUpload: "Téléverser des Documents",
  acAttend: "Accompagnez-Moi",
  acAI: "Demander à Soarlife AI",
  supportOptions: "Options de soutien",
  upcomingAppointments: "Rendez-vous à venir",
  noAppointments:
    "Votre infirmière ajoutera vos rendez-vous ici — les rappels de préparation sont envoyés par email automatiquement.",
  automaticReminders: "Rappels automatiques",
  remindersIntro: "Pour chaque rendez-vous, nous vous envoyons par email des listes de préparation :",
  reminderMilestones: [
    ["30 jours", "Formulaires · dossiers · assurance"],
    ["14 jours", "Historique médical · médicaments"],
    ["7 jours", "Documents · instructions"],
    ["3 jours", "Questions pour le médecin"],
    ["24 h", "Rendez-vous · médicaments · transport"],
  ],
  wellnessTitle: "Bilan de bien-être quotidien",
  moods: ["Très difficile", "Journée difficile", "Ça va", "Pleine d'espoir", "Très bien"],
  checkinPlaceholder: "Comment vous sentez-vous aujourd'hui ? (note optionnelle)",
  saveCheckin: "Enregistrer",
  checkinSaved: "Enregistré — prenez soin de vous",
  myQuestions: "Mes questions pour le médecin",
  noQuestions:
    "Utilisez un bouton « Générer des questions pour mon médecin » — vos questions se rassemblent ici.",
  deniedTitle: "La Feuille de Route est réservée aux patientes",
  deniedBody:
    "Entrez votre code d'accès Soarlife dans le Portail Patiente pour débloquer votre Feuille de Route Fertilité.",
  deniedCta: "Aller au Portail Patiente",
};
